import { env } from "../env";
import { getProject } from "../utils/local-storage";
import { trackV2 } from "../backend/tracking/v2";

// Per-user switch for the new UI. Off by default.
// Turn on with ?ui=new, off with ?ui=classic; the choice is remembered in localStorage.
const KEY = "mt-ui";

/** `REACT_APP_UI_MODE=new` makes the new UI the only one: no switch, `?ui=classic` ignored */
const onlyNewUI = () => env.REACT_APP_UI_MODE === "new";
/** `REACT_APP_UI_MODE=default` opens the new UI for anyone who hasn't chosen; the switch back stays */
const newUIByDefault = () => env.REACT_APP_UI_MODE === "default";

function readFromUrl(): "new" | "classic" | null {
    const value = new URLSearchParams(window.location.search).get("ui");
    return value === "new" || value === "classic" ? value : null;
}

export function isNewUI(): boolean {
    if (onlyNewUI()) return true;
    const fromUrl = readFromUrl();
    if (fromUrl) {
        localStorage.setItem(KEY, fromUrl);
    }
    const chosen = localStorage.getItem(KEY);
    return chosen ? chosen === "new" : newUIByDefault();
}

/** Flip to offer the new UI to every user; until then only people who opened it once see the switch */
const OFFER_TO_EVERYONE = false;

/**
 * Beta projects whose members are offered the new UI: comma-separated project ids in
 * `REACT_APP_NEW_UI_BETA_PROJECTS`, or `*` for everyone. Empty or missing offers it to no one.
 */
function isBetaProject(projectId?: string): boolean {
    const list = (env.REACT_APP_NEW_UI_BETA_PROJECTS || "").split(",").map(id => id.trim()).filter(Boolean);
    const id = projectId || getProject()?.id;
    return list.includes("*") || (!!id && list.includes(id));
}

/** Whether the classic UI should offer a way into the new one */
export function canSwitchToNewUI(projectId?: string): boolean {
    if (onlyNewUI()) return false;
    return OFFER_TO_EVERYONE || newUIByDefault() || localStorage.getItem(KEY) !== null || isBetaProject(projectId);
}

/** Whether the new UI should offer a way back to the classic one */
export const canSwitchToClassic = () => !onlyNewUI();

/** Switches UI; `to` is where to land (the new UI opens on a project's Overview), else the same page reloads */
export function setNewUI(on: boolean, to?: string) {
    localStorage.setItem(KEY, on ? "new" : "classic");
    trackV2(on ? "v2_switch_to_new" : "v2_switch_to_classic", { from: window.location.pathname });
    // Layouts are chosen at render time, so a full load swaps them cleanly
    if (to) window.location.assign(to);
    else window.location.reload();
}

/** A project's landing page after sign-in: Overview in the new UI, Environments in the classic one */
export function projectHome(projectId: string): string {
    return `/projects/${projectId}/${isNewUI() ? "overview" : "environments"}`;
}

/** Where "View in Pipelines" after a deploy leads: the classic feed, or Activity in the new UI */
export function deploysLink(projectId: string): { href: string; label: string } {
    return isNewUI()
        ? { href: `/projects/${projectId}/activity`, label: "View in Activity" }
        : { href: `/projects/${projectId}/pipelines`, label: "View in Pipelines" };
}
